import { CommonModule, FormatWidth, getLocaleDateFormat } from '@angular/common';
import { Component, effect, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { ApiPurpleFitCompleteRegistrationRequest, ApiPurpleFitUpdateUserInfoRequest, PRPVUserFull } from '../../../core-modules/sdk';

import { LayoutService } from '../../layout/layout.service';
import { Location } from '@angular/common';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { environment } from '../../../environments/default/environment';
import { PurpleTranslationPipe, PurpleTranslationService } from '@purple-lib/purple-translations-core';
import { PurpleFormComponent, PurpleFormFieldsGroup, PurpleGetPurpleSelectByIdRequest, PurpleGetPurpleSelectByNameRequest, PurpleSimpleButtonComponent } from '@purple-lib/purple-ui-widgets';
import { PurpleEditableImageDirective } from '@purple-lib/purple-edit-medias';
import { PurpleEditableTextDirective } from '@purple-lib/purple-edit-translations';
import { PurplePolicyFormComponent, ApiPolicyCheck } from '@purple-lib/purple-policies-core';
import { PurpleUserService, ApiRegisterRequest } from '@purple-lib/purple-users-core';
import { PurpleBreakpointsService, Helper, PurpleDateExtensions, PurpleStringExtensions } from '@purple-lib/purple-utilities';
import { AddressManagerService } from '@purple-lib/purple-addresses-core';
import { PurpleFitCoreTranslations } from '../../../core-modules/constants/purple-fit-core-translations';
import { provideNzIconsPatch } from 'ng-zorro-antd/icon';
import { ArrowLeftOutline } from '@ant-design/icons-angular/icons';

@Component({
  selector: 'app-complete-register',
  templateUrl: './complete-register.component.html',
  styleUrls: ['../authentication.scss', '../register/register.component.scss', './complete-register.component.scss'],
  imports: [CommonModule, FormsModule, PurpleTranslationPipe, PurpleEditableTextDirective, PurpleEditableImageDirective, PurpleFormComponent,
    PurpleSimpleButtonComponent, NzTabsModule, PurplePolicyFormComponent],
  providers: [provideNzIconsPatch([ArrowLeftOutline])]
})
export class CompleteRegisterComponent implements OnInit, OnDestroy {
  protected readonly T = PurpleFitCoreTranslations;

  constructor(@Inject(LOCALE_ID) private locale: string, private fb: FormBuilder, private location: Location,
    public breakSvc: PurpleBreakpointsService, private addSvc: AddressManagerService, private router: Router, public tsvc: PurpleTranslationService,
    private userSvc: PurpleUserService<PRPVUserFull, ApiRegisterRequest, ApiPurpleFitCompleteRegistrationRequest, ApiPurpleFitUpdateUserInfoRequest>,
    private layoutSvc: LayoutService, private route: ActivatedRoute) {
    this.validateForm = this.fb.group({});
    effect(async () => {
      const device = this.breakSvc.breakPointDevice(); // This signal dependency is tracked automatically

      if (this.userSvc.exceptionUser == undefined) {
        this.location.historyGo(1);
        await this.router.navigate([this.tsvc.currentLanguage(), 'login']);
        return; // Early return to prevent form generation if user is missing
      }

      if (device == "mobile" || device == "tablet") {
        // ... (Your existing Mobile/Tablet Logic)
        this.purpleFormFieldGroups = [
          {
            fieldGroupNumber: 1,
            fieldGroupBootstrapColumn: 12,
            fieldGroupPaddingLeft: false,
            fieldGroupPaddingRight: false,
            formFieldFormGroup: this.validateForm,
            formFields: [
              {
                fieldType: 'input-text',
                fieldControlName: 'firstname',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_firstname, []),
                fieldPlaceholder: '',
                fieldValue: this.userSvc.exceptionUser?.firstName ?? undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'lastname',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_lastname, []),
                fieldPlaceholder: "",
                fieldValue: this.userSvc.exceptionUser?.lastName ?? undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'email',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_email, []),
                fieldPlaceholder: "",
                fieldValue: this.userSvc.exceptionUser?.email ?? undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldIsDisabled: true,
                fieldCustomValidators: [Validators.email],
                fieldCustomValidatorErrors: [{ errorId: "email", text: this.tsvc.getTranslationValue(this.T.invalid_email_validator_error_text) }]
              },
              {
                fieldType: 'date',
                fieldControlName: 'birthdate',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_birthdate, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [
                  { id: 'dateFormat', value: getLocaleDateFormat(this.locale, FormatWidth.Medium) },
                  { id: 'showTime', value: false }
                ]
              },
              {
                fieldType: 'select-local',
                fieldControlName: 'gender',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_gender, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [
                  { id: 'isMultiple', value: false },
                  { id: 'showArrow', value: true },
                  {
                    id: 'options', value: [
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_gender_man, []),
                        value: "M"
                      },
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_gender_female, []),
                        value: "F"
                      },
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_other, []),
                        value: "O"
                      }
                    ]
                  }
                ]
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'taxid',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_vatid, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false
              },
              {
                fieldType: 'select',
                fieldControlName: 'address',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_address, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [
                  { id: 'searchFunction', value: this.addressSearchFunction },
                  { id: 'searchByIdFunction', value: this.addressSearchIdFunction },
                  { id: 'refreshFieldNumber', value: 0 },
                ]
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'phone',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_phone, []),
                fieldPlaceholder: "",
                fieldValue: this.userSvc.exceptionUser?.phone ?? undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldAffix: {
                  fieldControlName: "phonePrefix",
                  fieldPlaceholder: this.tsvc.getTranslationValue(this.T.register_page_form_phone_prefix_placeholder, []),
                  fieldType: "select-local",
                  fieldWidth: "90px",
                  fieldIsRequired: true,
                  fieldValue: this.userSvc.exceptionUser?.phonePrefix ?? undefined,
                  fieldOptions: [
                    { id: 'type', value: 'default' },
                    { id: 'showArrow', value: true },
                    {
                      id: 'options', value: this.phonePrefixes
                    }
                  ]
                },
              },
              {
                fieldType: 'textarea',
                fieldControlName: 'injuries',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_injuries, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: false,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [{ id: 'rowNumber', value: 4 }]
              },
              // {
              //   fieldType: 'input-text',
              //   fieldControlName: 'promo',
              //   fieldBootstrapColumn: 12,
              //   fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_promo_code, []),
              //   fieldPlaceholder: "",
              //   fieldValue: undefined,
              //   fieldIsRequired: false,
              //   fieldIsDisabled: false,
              //   fieldPaddingLeft: false,
              //   fieldPaddingRight: false
              // }
            ]
          }
        ]
      } else {
        this.purpleFormFieldGroups = [
          {
            fieldGroupNumber: 1,
            fieldGroupBootstrapColumn: 12,
            fieldGroupPaddingLeft: false,
            fieldGroupPaddingRight: false,
            formFieldFormGroup: this.validateForm,
            formFields: [
              {
                fieldType: 'input-text',
                fieldControlName: 'firstname',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_firstname, []),
                fieldPlaceholder: "",
                fieldValue: this.userSvc.exceptionUser?.firstName ?? undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: true
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'lastname',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_lastname, []),
                fieldPlaceholder: "",
                fieldValue: this.userSvc.exceptionUser?.lastName ?? undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: true,
                fieldPaddingRight: false
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'email',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_email, []),
                fieldPlaceholder: "",
                fieldValue: this.userSvc.exceptionUser?.email ?? undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldIsDisabled: true,
                fieldCustomValidators: [Validators.email],
                fieldCustomValidatorErrors: [{ errorId: "email", text: this.tsvc.getTranslationValue(this.T.invalid_email_validator_error_text) }]
              },
              {
                fieldType: 'input-text',
                fieldControlName: 'taxid',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_vatid, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: true
              },
              {
                fieldType: 'date',
                fieldControlName: 'birthdate',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_birthdate, []),
                fieldPlaceholder: " ",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldIsDisabled: false,
                fieldPaddingLeft: true,
                fieldPaddingRight: false,
                fieldOptions: [
                  { id: 'dateFormat', value: getLocaleDateFormat(this.locale, FormatWidth.Medium) },
                  { id: 'showTime', value: false }
                ]
              }
            ]
          }
        ]

        this.purpleFormFieldGroups2 = [
          {
            fieldGroupNumber: 1,
            fieldGroupBootstrapColumn: 12,
            fieldGroupPaddingLeft: false,
            fieldGroupPaddingRight: false,
            formFieldFormGroup: this.validateForm,
            formFields: [
              {
                fieldType: 'select-local',
                fieldControlName: 'gender',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_gender, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: false,
                fieldPaddingRight: true,
                fieldOptions: [
                  { id: 'isMultiple', value: false },
                  { id: 'showArrow', value: true },
                  {
                    id: 'options', value: [
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_gender_man, []),
                        value: "M"
                      },
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_gender_female, []),
                        value: "F"
                      },
                      {
                        label: this.tsvc.getTranslationValue(this.T.register_page_form_other, []),
                        value: "O"
                      }
                    ]
                  }
                ]
              },
                {
                fieldType: 'input-text',
                fieldControlName: 'phone',
                fieldBootstrapColumn: 6,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_phone, []),
                fieldPlaceholder: "",
                fieldValue: this.userSvc.exceptionUser?.phone ?? undefined,
                fieldIsRequired: true,
                fieldPaddingLeft: true,
                fieldPaddingRight: false,
                fieldAffix: {
                  fieldControlName: "phonePrefix",
                  fieldPlaceholder: this.tsvc.getTranslationValue(this.T.register_page_form_phone_prefix_placeholder, []),
                  fieldType: "select-local",
                  fieldWidth: "90px",
                  fieldIsRequired: true,
                  fieldValue: this.userSvc.exceptionUser?.phonePrefix ?? undefined,
                  fieldOptions: [
                    { id: 'type', value: 'default' },
                    { id: 'showArrow', value: true },
                    {
                      id: 'options', value: this.phonePrefixes
                    }
                  ]
                },
               }, 
              {
                fieldType: 'select',
                fieldControlName: 'address',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_address, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: true,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [
                  { id: 'searchFunction', value: this.addressSearchFunction },
                  { id: 'searchByIdFunction', value: this.addressSearchIdFunction },
                  { id: 'refreshFieldNumber', value: 0 },
                ]
              },
            
              //{
              //   fieldType: 'input-text',
              //   fieldControlName: 'promo',
              //   fieldBootstrapColumn: 6,
              //   fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_promo_code, []),
              //   fieldPlaceholder: "",
              //   fieldValue: undefined,
              //   fieldIsRequired: false,
              //   fieldIsDisabled: false,
              //   fieldPaddingLeft: true,
              //   fieldPaddingRight: false
              // },
              {
                fieldType: 'textarea',
                fieldControlName: 'injuries',
                fieldBootstrapColumn: 12,
                fieldName: this.tsvc.getTranslationValue(this.T.register_page_form_injuries, []),
                fieldPlaceholder: "",
                fieldValue: undefined,
                fieldIsRequired: false,
                fieldIsDisabled: false,
                fieldPaddingLeft: false,
                fieldPaddingRight: false,
                fieldOptions: [{ id: 'rowNumber', value: 2 }]
              }
            ]
          }
        ]
      }
    });
  }

  validateForm!: FormGroup;
  purpleFormFieldGroups: PurpleFormFieldsGroup[] | undefined;
  purpleFormFieldGroups2: PurpleFormFieldsGroup[] | undefined;
  phonePrefixes: { label: string, value: string }[] = Helper.getPhonePrefixes().map(m => { return { label: m, value: m } });
  currentTab: number = 0;

  buttonText: string = "";
  stepSubTitle: string = "";

  sub: Subject<void> = new Subject();
  isLoading: boolean = false;
  formPolicies: ApiPolicyCheck[] = [];

  ngOnDestroy(): void {
    this.sub.next();
    this.sub.complete();
  }

  ngOnInit() {
    this.route.queryParams.subscribe(async params => {
      this.layoutSvc.setRedirectUrl(params, false);
    });

    this.buttonText = this.tsvc.getTranslationValue(this.T.register_page_register_step_1_button, [])
    this.stepSubTitle = this.tsvc.getTranslationValue(this.T.register_page_desktop_step_1_form_subtitle, [])


  }
  back() {
    this.currentTab = 0;
    this.onTabChange(this.currentTab);
  }

  onTabChange(idx: number) {
    if (idx == 0) {
      this.buttonText = this.tsvc.getTranslationValue(this.T.register_page_register_step_1_button, [])
      this.stepSubTitle = this.tsvc.getTranslationValue(this.T.register_page_desktop_step_1_form_subtitle, [])
    } else {
      this.buttonText = this.tsvc.getTranslationValue(this.T.register_page_register_step_2_button, [])
      this.stepSubTitle = this.tsvc.getTranslationValue(this.T.register_page_desktop_step_2_form_subtitle, [])
    }
  }

  async completeRegister() {
    if (this.currentTab == 0 && this.breakSvc.breakPointDevice() == 'desktop') {
      let isStepValid = true;

      this.purpleFormFieldGroups![0].formFields.forEach(element => {
        this.validateForm.controls[element.fieldControlName].markAsDirty();
        this.validateForm.controls[element.fieldControlName].updateValueAndValidity();

        if (isStepValid == true) {
          if (!PurpleStringExtensions.strEq(this.validateForm.controls[element.fieldControlName].status, "DISABLED")) {
            isStepValid = this.validateForm.controls[element.fieldControlName].valid;
          }
        }
      });

      if (isStepValid) {
        this.currentTab++;
      }

    } else {
      this.isLoading = true;
      for (const i in this.validateForm.controls) {
        this.validateForm.controls[i].markAsDirty();
        this.validateForm.controls[i].updateValueAndValidity();
      }

      if (this.validateForm.valid) {

        await this.userSvc.completeRegistration({
          firstName: this.validateForm.controls["firstname"].value,
          lastName: this.validateForm.controls["lastname"].value,
          externalAddressId: this.validateForm.controls["address"].value,
          phonePrefix: this.validateForm.controls["phonePrefix"].value,
          phone: this.validateForm.controls["phone"].value,
          taxId: this.validateForm.controls["taxid"].value,
          birthDate: PurpleDateExtensions.toIsoStringPurple(this.validateForm.controls["birthdate"].value as Date),
          genderId: this.validateForm.controls["gender"].value,
          injuries: this.validateForm.controls["injuries"].value,
          policies: this.formPolicies,
          clientTypeId: "web",
          clientId: environment.appName,
          clientVersion: environment.appVersion,
          skipValidateRequest: false
        }, () => {
          console.log("Complete registration success");
          if (this.layoutSvc.redirectUrl != undefined) {
            this.router.navigateByUrl(this.layoutSvc.redirectUrl);
          } else {
            this.router.navigate([this.tsvc.currentLanguage(), environment.CLIENT_ROUTE, 'home']);
          }
        });
      }
      this.isLoading = false;
    }
  }


  //#region Address
  addressSearchFunction = (culture: string, args: PurpleGetPurpleSelectByNameRequest) => {
    return this.addSvc.getAddressAutocompleteByNameSelect(culture, args)
  }

  addressSearchIdFunction = (culture: string, args: PurpleGetPurpleSelectByIdRequest) => {
    return this.addSvc.getAddressAutocompleteByIdSelect(culture, args)
  }
  //#endregion

}
